/**
 * Canvas Object Adapter
 *
 * Moves all user objects on the live Fabric.js canvas to a given placement on
 * a PrintContext. The caller decides the placement (see canvasStateManager's
 * switch anchor); this module only applies it.
 *
 * Key design choice: objects are treated as a GROUP. The group's bounding box
 * is the reference. Individual objects keep their relative positions within
 * the group, scaled uniformly.
 *
 * Invariant: ONE scale factor for both axes. Automatic adaptation never
 * distorts a design; a distortion the user applied survives unchanged.
 */

import type { Canvas as FabricCanvas, FabricObject } from 'fabric';
import type { RelativeDesignState } from '@designdeck/shared';
import type { PrintContext } from '@/types/printContext';
import { getUserObjects } from '@/services/canvas';
import { logger } from '@/utils/logger';

// =============================================================================
// TYPES
// =============================================================================

export interface AdaptCanvasObjectsParams {
  canvas: FabricCanvas;
  /** Print area the objects are moved onto (offsets are canvas coordinates) */
  targetContext: PrintContext;
  /** Where the group lands on the target print area (0-1, center-based) */
  targetPlacement: RelativeDesignState;
}

// =============================================================================
// MAIN FUNCTION
// =============================================================================

/**
 * Move all user objects on the canvas to `targetPlacement` on `targetContext`.
 *
 * Algorithm:
 * 1. Compute group AABB of all user objects (getBoundingRect)
 * 2. Derive ONE scale factor from the placement's width
 * 3. Compute the target group AABB in canvas coordinates
 * 4. Apply scale + translation to each object (preserve relative positions)
 * 5. Call obj.setCoords() after each transform
 */
export function adaptCanvasObjects(params: AdaptCanvasObjectsParams): void {
  const { canvas, targetContext, targetPlacement: targetRel } = params;

  const objects = getUserObjects(canvas);
  if (objects.length === 0) return;

  // 1. Compute group AABB
  const groupAABB = computeGroupAABB(objects);
  if (groupAABB.width <= 0 || groupAABB.height <= 0) return;

  // 2. Uniform scale factor: width-derived, applied to BOTH axes.
  // The placement's designRatio and the live bbox can drift apart by rounding;
  // deriving a second factor from the height would turn that drift into a
  // distortion. The group's real height follows from the same factor.
  const targetGroupWidth = targetRel.relWidth * targetContext.widthPx;
  if (targetGroupWidth <= 0) {
    logger.warn(
      'canvasObjectAdapter',
      'Target dimensions are zero/negative, skipping adaptation'
    );
    return;
  }
  const scale = targetGroupWidth / groupAABB.width;
  const targetGroupHeight = groupAABB.height * scale;

  // 3. Target group AABB in canvas coordinates, centered on the placement center
  const targetGroupLeft =
    targetContext.offsetX +
    targetRel.relCenterX * targetContext.widthPx -
    targetGroupWidth / 2;
  const targetGroupTop =
    targetContext.offsetY +
    targetRel.relCenterY * targetContext.heightPx -
    targetGroupHeight / 2;

  logger.debug('canvasObjectAdapter', 'Adapting objects', {
    objectCount: objects.length,
    sourceAABB: groupAABB,
    targetGroupLeft,
    targetGroupTop,
    targetGroupWidth,
    targetGroupHeight,
    scale,
  });

  // 4. Transform each object: similarity transform about the group's top-left.
  // obj.left/top is the transform origin, groupAABB comes from getBoundingRect;
  // under a uniform scale both move by the same factor, so this is exact.
  for (const obj of objects) {
    obj.left = targetGroupLeft + (obj.left - groupAABB.left) * scale;
    obj.top = targetGroupTop + (obj.top - groupAABB.top) * scale;

    obj.scaleX = (obj.scaleX ?? 1) * scale;
    obj.scaleY = (obj.scaleY ?? 1) * scale;

    obj.setCoords();
  }
}

// =============================================================================
// HELPERS
// =============================================================================

interface AABB {
  left: number;
  top: number;
  width: number;
  height: number;
}

function computeGroupAABB(objects: FabricObject[]): AABB {
  let minLeft = Infinity;
  let minTop = Infinity;
  let maxRight = -Infinity;
  let maxBottom = -Infinity;

  for (const obj of objects) {
    const rect = obj.getBoundingRect();
    minLeft = Math.min(minLeft, rect.left);
    minTop = Math.min(minTop, rect.top);
    maxRight = Math.max(maxRight, rect.left + rect.width);
    maxBottom = Math.max(maxBottom, rect.top + rect.height);
  }

  return {
    left: minLeft,
    top: minTop,
    width: maxRight - minLeft,
    height: maxBottom - minTop,
  };
}
